import React, { useEffect, useState, useRef } from 'react';
import FadeLoader from 'react-spinners/FadeLoader';
import { RxDotsVertical } from 'react-icons/rx';
import { HiOutlineUserGroup } from 'react-icons/hi';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import { deleteEmployeeWithAllData } from '../../Services/ManagerService.jsx';
import EmployeeRegistration from '../Employee/EmployeeRegistration.jsx';
import OwnerParkinglotRegistration from './OwnerParkinglotRegistration.jsx';
import { GetUserDetailsFromToken } from '../../Services/employeeService.jsx';
import './OwnerRegistration.css';

const formatRegisteredOn = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getEmployeeField = (employee, ...keys) => {
  if (!employee) return undefined;
  for (const key of keys) {
    if (employee[key] != null && employee[key] !== '') return employee[key];
  }
  return undefined;
};

const normalizeEmployee = (employee) => {
  if (!employee) return null;
  return {
    ...employee,
    emailId: getEmployeeField(employee, 'emailId', 'EmailId', 'email'),
    firstName: getEmployeeField(employee, 'firstName', 'empFirstName', 'FirstName', 'EmpFirstName'),
    lastName: getEmployeeField(employee, 'lastName', 'empLastName', 'LastName', 'EmpLastName'),
    mobileNumber: getEmployeeField(employee, 'mobileNumber', 'MobileNumber'),
    empRegistrationID: getEmployeeField(employee, 'empRegistrationID', 'EmpRegistrationID', 'empRegistrationId'),
    empId: getEmployeeField(employee, 'empId', 'EmpId', 'empID'),
    createdDate: getEmployeeField(employee, 'createdDate', 'CreatedDate', 'registeredOn'),
    status: getEmployeeField(employee, 'status', 'empStatus', 'Status') || 'Active',
  };
};

const EmployeeCard = ({
  employee,
  activeMenuId,
  onActiveMenuIdChange,
  onEditDetails,
  onAddProperty,
  onDeleteEmployee,
  onSelectEmployee,
  hideMenu = false,
}) => {
  const menuId = employee.empRegistrationID || employee.emailId;
  const showMenu = !hideMenu && activeMenuId === menuId;
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const dotsRef = useRef(null);
  const menuRef = useRef(null);

  const fullName = `${employee.firstName || employee.empFirstName || ''} ${employee.lastName || employee.empLastName || ''}`.trim() || '-';
  const status = employee.status || employee.empStatus || 'Active';
  const isActive = String(status).toLowerCase() === 'active';

  const handleDotsClick = (event) => {
    event.stopPropagation();
    if (dotsRef.current) {
      const { top, left, height } = dotsRef.current.getBoundingClientRect();
      const parentCard = dotsRef.current.closest('.property-card');
      const parentCardRect = parentCard.getBoundingClientRect();

      setMenuPosition({
        top: top - parentCardRect.top + height,
        left: left - parentCardRect.left - 150,
      });
      onActiveMenuIdChange(showMenu ? null : menuId);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && dotsRef.current && !dotsRef.current.contains(event.target)) {
        onActiveMenuIdChange(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [onActiveMenuIdChange]);

  return (
    <div
      className="property-card"
      style={{ position: 'relative', cursor: onSelectEmployee ? 'pointer' : 'default' }}
      onClick={() => onSelectEmployee?.(employee)}
    >
      <div className="property-content">
        <div className="owner-details">
          <HiOutlineUserGroup className="employee-card-icon" size={48} />
        </div>
        <div className="property-info">
          <div className="info-block">
            <p className="info-header">Team Member Name</p>
            <p className="info-data">{fullName}</p>
          </div>
          <div className="info-block">
            <p className="info-header">Email</p>
            <p className="info-data">{employee.emailId || '-'}</p>
          </div>
          <div className="info-block">
            <p className="info-header">Mobile Phone</p>
            <p className="info-data">{employee.mobileNumber || '-'}</p>
          </div>
          <div className="info-block">
            <p className="info-header">Registered On</p>
            <p className="info-data">{formatRegisteredOn(employee.createdDate || employee.registeredOn)}</p>
          </div>
          <div className="info-block">
            <p className="info-header">Status</p>
            <p className={`info-data ${isActive ? 'employee-status-active' : ''}`}>{status}</p>
          </div>
        </div>
        {!hideMenu && (
          <div className="dots-menu" onClick={handleDotsClick} style={{ cursor: 'pointer' }} ref={dotsRef}>
            <RxDotsVertical size={28} />
          </div>
        )}
        {showMenu && (
          <div
            className="dropdown-menu1"
            ref={menuRef}
            style={{
              position: 'absolute',
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}
          >
            <div className="dropdown-option" onClick={(e) => onEditDetails(e, employee)}>Edit Details</div>
            <div className="dropdown-option" onClick={(e) => onAddProperty(e, employee)}>Add Property</div>
            <div className="dropdown-option" onClick={(e) => onDeleteEmployee(e, employee)}>Delete Employee</div>
          </div>
        )}
      </div>
    </div>
  );
};

const OwnerTeamMembers = ({
  entityId,
  OwnerDetails,
  handleEdit,
  onEditEmployeeModeChange,
  viewingEmployee,
  onViewingEmployeeChange,
  expandEmpRegistrationID,
  onSetAreasMarked,
  onSetParkingLotObj,
  onSetDefaultAddress,
  onSetEditOption,
  handleSetEdit,
}) => {
  const hostPath = process.env.REACT_APP_API_BASE_URL;
  const hostHeader = process.env.REACT_APP_CUSTOM_HEADER;
  const [employees, setEmployees] = useState([]);
  const [loader, setLoader] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [registerEmployee, setRegisterEmployee] = useState(false);
  const [employeeEmailId, setEmployeeEmailId] = useState(null);
  const [empRegistrationId, setEmpRegistrationId] = useState(null);
  const [empb2cId, setEmpb2cId] = useState(null);
  const [ownerId, setOwnerId] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await GetUserDetailsFromToken();
        setOwnerId(userData?.empId || OwnerDetails?.userId);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setOwnerId(OwnerDetails?.userId);
      }
    };
    fetchUserData();
  }, [OwnerDetails]);

  useEffect(() => {
    onEditEmployeeModeChange?.(registerEmployee);
  }, [registerEmployee, onEditEmployeeModeChange]);

  const fetchEmployees = async () => {
    if (!entityId) {
      setEmployees([]);
      return;
    }

    setLoader(true);
    try {
      const response = await fetch(`${hostPath}/api/GetEmpdetailsbyOwner`, {
        method: 'POST',
        headers: {
          'x-functions-key': hostHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entityRegistrationID: entityId }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          setEmployees([]);
          return;
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setEmployees(Array.isArray(data) ? data.map(normalizeEmployee) : []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      setEmployees([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [entityId, hostPath, hostHeader, refreshKey]);

  // After returning from Add Property, auto-expand that employee's property list
  useEffect(() => {
    if (!expandEmpRegistrationID || !employees.length || viewingEmployee) return;
    const match = employees.find(
      (emp) => String(emp.empRegistrationID) === String(expandEmpRegistrationID)
    );
    if (match) {
      onViewingEmployeeChange?.(match);
    }
  }, [expandEmpRegistrationID, employees, viewingEmployee, onViewingEmployeeChange]);

  const closeEmployeeForm = () => {
    setRegisterEmployee(false);
    setEmployeeEmailId(null);
    setEmpRegistrationId(null);
    setEmpb2cId(null);
  };

  const handleRegisterEmployee = (eOrVal, val, email, empRegId, empId) => {
    // Cancel / close patterns from EmployeeRegistration: (false) or (null, false)
    if (eOrVal === false || eOrVal === null || val === false) {
      closeEmployeeForm();
      return;
    }
    if (eOrVal && typeof eOrVal === 'object' && eOrVal.stopPropagation) {
      eOrVal.stopPropagation();
    }
    setRegisterEmployee(val === true);
    if (email) setEmployeeEmailId(email);
    if (empRegId) setEmpRegistrationId(empRegId);
    if (empId) setEmpb2cId(empId);
  };

  const buildEmployeeUserData = (employee) => {
    const normalized = normalizeEmployee(employee);
    return {
      ...OwnerDetails,
      ...normalized,
      firstName: normalized?.firstName || OwnerDetails?.firstName,
      emailId: normalized?.emailId,
      mobileNumber: normalized?.mobileNumber,
      empRegistrationID: normalized?.empRegistrationID,
      EmpRegistrationID: normalized?.empRegistrationID,
      empId: normalized?.empId,
      EmpId: normalized?.empId,
      entityRegistrationID: OwnerDetails?.entityRegistrationID,
      enityMailingAdderess: OwnerDetails?.enityMailingAdderess,
    };
  };

  const handleEditDetails = (e, employee) => {
    if (e) e.stopPropagation();
    setActiveMenuId(null);
    const normalized = normalizeEmployee(employee);
    setEmployeeEmailId(normalized?.emailId);
    setEmpRegistrationId(normalized?.empRegistrationID);
    setEmpb2cId(normalized?.empId);
    setRegisterEmployee(true);
  };

  const handleAddProperty = (e, employee) => {
    if (e) e.stopPropagation();
    setActiveMenuId(null);
    const target = normalizeEmployee(employee || viewingEmployee);
    if (handleEdit && target?.empRegistrationID) {
      handleEdit(e, buildEmployeeUserData(target));
    } else if (handleEdit) {
      alert('Unable to add property: employee registration id is missing.');
    }
  };

  const handleSelectEmployee = (employee) => {
    setActiveMenuId(null);
    const normalized = normalizeEmployee(employee);
    const employeeKey = normalized?.empRegistrationID || normalized?.emailId;
    const current = normalizeEmployee(viewingEmployee);
    const currentKey = current?.empRegistrationID || current?.emailId;
    // Toggle: click same employee again to collapse properties under them
    onViewingEmployeeChange?.(employeeKey === currentKey ? null : normalized);
  };

  const handleDeleteEmployee = (e, employee) => {
    if (e) e.stopPropagation();
    setActiveMenuId(null);
    setEmployeeToDelete(normalizeEmployee(employee));
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async (action) => {
    if (action === 'cancel') {
      setOpenDeleteDialog(false);
      setEmployeeToDelete(null);
      return;
    }

    const email = employeeToDelete?.emailId;
    if (!email) {
      alert('Unable to delete employee: email is missing.');
      return;
    }

    setLoader(true);
    try {
      const result = await deleteEmployeeWithAllData(
        email,
        employeeToDelete.empId,
        employeeToDelete.empRegistrationID
      );
      if (result.success) {
        setOpenDeleteDialog(false);
        const deletedKey = employeeToDelete.empRegistrationID || employeeToDelete.emailId;
        setEmployeeToDelete(null);
        const viewing = normalizeEmployee(viewingEmployee);
        const viewingKey = viewing?.empRegistrationID || viewing?.emailId;
        if (deletedKey && deletedKey === viewingKey) {
          onViewingEmployeeChange?.(null);
        }
        setRefreshKey((prev) => prev + 1);
      } else {
        alert(result.message || 'Failed to delete employee.');
      }
    } catch (error) {
      console.error('Error during employee deletion:', error);
      alert(error.message || 'Failed to delete employee.');
    } finally {
      setLoader(false);
    }
  };

  const employeeName = employeeToDelete
    ? `${employeeToDelete.firstName || employeeToDelete.empFirstName || ''} ${employeeToDelete.lastName || employeeToDelete.empLastName || ''}`.trim()
    : '';

  if (registerEmployee) {
    return (
      <div>
        {loader && (
          <div className="loader-overlay">
            <FadeLoader />
          </div>
        )}
        <EmployeeRegistration
          registerEmployee={registerEmployee}
          handleRegisterEmployee={handleRegisterEmployee}
          empb2cId={empb2cId}
          ownerId={ownerId || OwnerDetails?.userId}
          userDetails={OwnerDetails}
          employeeEmailId={employeeEmailId}
          empRegistrationId={empRegistrationId}
          refreshEmployees={() => setRefreshKey((prev) => prev + 1)}
          disableRedirect
        />
      </div>
    );
  }

  return (
    <div>
      {loader && (
        <div className="loader-overlay">
          <FadeLoader />
        </div>
      )}

      <div className="container-fluid p-0">
        <div className="lotmanagement">
          <div className="employees-section-divider">
            <hr />
            <h3>Employees</h3>
          </div>

          {!loader && employees.length === 0 ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '160px' }}>
              <h5 style={{ color: 'black' }}>No team members found for this owner</h5>
            </div>
          ) : (
            employees.map((employee, index) => {
              const employeeKey = employee.empRegistrationID || employee.emailId;
              const viewingKey = viewingEmployee?.empRegistrationID || viewingEmployee?.emailId;
              const isExpanded = employeeKey && employeeKey === viewingKey;

              return (
                <div key={employeeKey || index}>
                  <EmployeeCard
                    employee={employee}
                    activeMenuId={activeMenuId}
                    onActiveMenuIdChange={setActiveMenuId}
                    onEditDetails={handleEditDetails}
                    onAddProperty={handleAddProperty}
                    onDeleteEmployee={handleDeleteEmployee}
                    onSelectEmployee={handleSelectEmployee}
                  />
                  {isExpanded && (
                    <OwnerParkinglotRegistration
                      entityId={OwnerDetails?.entityRegistrationID}
                      empRegistrationID={employee.empRegistrationID}
                      empId={employee.empId}
                      employeeView
                      onSetAreasMarked={onSetAreasMarked}
                      onSetParkingLotObj={onSetParkingLotObj}
                      onSetDefaultAddress={onSetDefaultAddress}
                      onSetEditOption={onSetEditOption}
                      handleSetEdit={handleSetEdit}
                      OwnerDetails={buildEmployeeUserData(employee)}
                      handleEdit={(e) => handleAddProperty(e, employee)}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <Dialog
        classes={{ paper: 'custom-paper-class-delete' }}
        aria-labelledby="delete-employee-dialog-title"
        open={openDeleteDialog}
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <DialogTitle
          sx={{ textAlign: 'start', color: '#000', fontSize: '24px', fontWeight: '700', padding: '5px 24px' }}
          id="delete-employee-dialog-title"
        >
          Are you Sure You Want to Delete This {employeeName} Employee?
        </DialogTitle>
        <DialogContent style={{ border: 'none', padding: '5px 22px', color: '#000' }}>
          <Typography>
            <span>If you delete this employee, all parking lots and related data associated with this employee will be deleted.</span>
          </Typography>
          <Typography gutterBottom>
            <span>This action is irreversible. Please confirm if you wish to proceed.</span>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'right' }}>
          <Button className="custom-button-cancel" onClick={() => handleConfirmDelete('cancel')}>Cancel</Button>
          <Button className="custom-button-delete" onClick={() => handleConfirmDelete('confirm')}>Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OwnerTeamMembers;
